import type { ClassificationDialogData, ClassificationDialogLabel, ClassificationDialogPolicy } from '@core/domain';
import { Badge, BadgeSize, BadgeTextSize } from '@soteria/design-system-ve';

import * as styles from './ClassificationDialog.css';

/** Normalize content type for case-insensitive comparison */
function normalizeContentType(contentType: string | undefined): string {
  return contentType?.toLowerCase() || 'unknown';
}

/** Build mapping from label IDs to the policy names that triggered them */
export function buildPolicyMappings(policies: ClassificationDialogPolicy[] | undefined) {
  const triggeredPolicyLabelIds = new Set<string>();
  const labelIdToPolicyNames = new Map<string, string[]>();

  if (policies && policies.length > 0) {
    policies.forEach(policy => {
      if (policy.labelIds) {
        policy.labelIds.forEach(labelId => {
          triggeredPolicyLabelIds.add(labelId);
          const existing = labelIdToPolicyNames.get(labelId);

          if (existing) {
            labelIdToPolicyNames.set(labelId, [...existing, policy.name]);
          } else {
            labelIdToPolicyNames.set(labelId, [policy.name]);
          }
        });
      }
    });
  }

  return { triggeredPolicyLabelIds, labelIdToPolicyNames };
}

/** Component for displaying triggered policies inline with a label */
function TriggeredPoliciesInfo({ policies }: { policies: string[] }) {
  if (policies.length === 0) return null;

  return (
    <div className={styles.categoryMetric}>
      <span className={styles.categoryLabel}>Triggered:</span>
      <div className={styles.policyBadgeContainer}>
        {policies.map(policyName => (
          <Badge key={policyName} size={BadgeSize.XSmall} text={policyName} textSize={BadgeTextSize.Small} />
        ))}
      </div>
    </div>
  );
}

/** Label Details Component for V2 */
function LabelDetailsV2({ label, triggeredByPolicies }: { label: ClassificationDialogLabel; triggeredByPolicies?: string[] }) {
  const normalizedType = normalizeContentType(label.contentType);
  const showSeverity = normalizedType === 'text' && label.severity !== undefined;
  const showScore = normalizedType === 'image' && label.score !== undefined;

  return (
    <div className={styles.categoryDetails}>
      {showSeverity && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      )}
      {showScore && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Score:</span>
          <span className={styles.severityValue}>{label.score?.toFixed(2)}%</span>
        </div>
      )}
      {label.contentType && (
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Content Type:</span>
          <span className={styles.severityValue}>{label.contentType}</span>
        </div>
      )}
      {triggeredByPolicies && <TriggeredPoliciesInfo policies={triggeredByPolicies} />}
    </div>
  );
}

/** Label Item Component for V2 — colour-coded per label based on triggered policy status */
function LabelItemV2({
  label,
  isTriggeredByPolicy,
  triggeredByPolicies,
}: {
  label: ClassificationDialogLabel;
  isTriggeredByPolicy: boolean;
  triggeredByPolicies?: string[];
}) {
  const styleClass = isTriggeredByPolicy ? styles.categoryItemUnsafe : styles.categoryItemSafe;

  return (
    <div className={`${styles.categoryItem} ${styleClass}`}>
      <div className={styles.categoryInfo}>
        <span className={styles.categoryName}>{label.name}</span>
      </div>
      <LabelDetailsV2 label={label} triggeredByPolicies={triggeredByPolicies} />
    </div>
  );
}

/** Group labels by content type */
function groupLabelsByContentType(labels: ClassificationDialogLabel[]) {
  const grouped = labels.reduce(
    (acc, label) => {
      const contentType = normalizeContentType(label.contentType);

      if (!acc[contentType]) {
        acc[contentType] = [];
      }

      acc[contentType].push(label);

      return acc;
    },
    {} as Record<string, ClassificationDialogLabel[]>
  );

  const sortedTypes = Object.keys(grouped).sort((a, b) => {
    const order: Record<string, number> = { image: 0, text: 1, username: 2, audio: 3, mixed: 4 };

    return (order[a] ?? 99) - (order[b] ?? 99);
  });

  return { grouped, sortedTypes };
}

/**
 * Labels Section for V2 Layout
 *
 * Matches V1 behaviour: each label is colour-coded based on whether it was triggered
 * by a policy (red border) or not (green border), and shows "Triggered:" with
 * policy badges inline per label.
 *
 * Falls back to overall isSafe colouring when policy-to-label mapping data is not available.
 */
export function LabelsSectionV2({ data }: { data: ClassificationDialogData }) {
  if (!data.labels || data.labels.length === 0) return null;

  const { triggeredPolicyLabelIds, labelIdToPolicyNames } = buildPolicyMappings(data.triggeredPolicies);
  const hasPolicyMappings = triggeredPolicyLabelIds.size > 0;
  const { grouped, sortedTypes } = groupLabelsByContentType(data.labels);

  return (
    <div className={`${styles.section} ${styles.labelsSection}`}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      {sortedTypes.map(contentType => (
        <div key={contentType} className={styles.contentTypeGroup}>
          <div className={styles.contentTypeHeader}>Content Type: {contentType.toUpperCase()}</div>
          <div className={styles.categoriesList}>
            {grouped[contentType].map(label => {
              // Per-label colouring when policy-to-label mapping is available, otherwise fall back to overall isSafe
              const isTriggeredByPolicy = hasPolicyMappings && label.labelId ? triggeredPolicyLabelIds.has(label.labelId) : !data.isSafe;
              const triggeredByPolicies = label.labelId ? labelIdToPolicyNames.get(label.labelId) : undefined;

              return (
                <LabelItemV2
                  key={label.labelId || `${contentType}-${label.name}-${label.severity}`}
                  isTriggeredByPolicy={isTriggeredByPolicy}
                  label={label}
                  triggeredByPolicies={triggeredByPolicies}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
